import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import flattenDeep from 'lodash/fp/flattenDeep';
import isEqual from 'lodash/fp/isEqual';
import useAccordion from 'app/hook/common/useAccordion';
import classnames from 'classnames/bind';
import styles from './Accordion.module.scss';

const cx = classnames.bind(styles);

const AccordionItem = ({ title = '', isShow = false, children = [], onClick = undefined, className = '' }) => (
	<li className={cx(className, { on: isShow })}>
		<button
			className={cx('btn_accordion')}
			type="button"
			onClick={onClick}
		>
			<span className={cx('text')}>{title}</span>
			<span className={cx('arr')}>자세히보기</span>
		</button>
		<div className={cx('inner')}>
			<div className={cx('item')}>{children}</div>
		</div>
	</li>
);

const Accordion = ({
	offsetTop = 0,
	useMulti = true,
	children = [],
	moreStyle = undefined,
	style = undefined,
	theme = undefined,
	showAll = false,
	showAllHide = false,
	useSpy = false,
	defaultViewCount = 5,
	useFirstOpen = false,
	className = '',
	showAllText = '모두보기',
}) => {
	const { accordionArray, setAccordionLength, handleAccordion, handleAccordionMulti } = useAccordion();

	const rootRef = useRef(null);
	const cloneChildren = useMemo(() => (Array.isArray(children) ? flattenDeep(children) : [children]), [children]);
	const [viewCount, setViewCount] = useState(0);
	const showAllHideFlag = showAllHide && viewCount === cloneChildren.length;

	const handleSetAccordion = useCallback(
		(current = 0) =>
			(e) => {
				const { target } = e;

				if (useMulti) {
					handleAccordionMulti(current);

					return;
				}

				handleAccordion(current, useSpy && { target, offsetTop: rootRef.current.offsetTop - offsetTop });
			},
		[handleAccordion, handleAccordionMulti, offsetTop, useMulti, useSpy],
	);

	const handleShowAll = useCallback(() => {
		setViewCount(cloneChildren.length);
	}, [cloneChildren.length]);

	const renderItems = useMemo(
		() =>
			cloneChildren.slice(0, viewCount).map((el, index) => {
				const isShow = accordionArray[index];

				return React.cloneElement(el, { isShow, onClick: handleSetAccordion(index) });
			}),
		[accordionArray, cloneChildren, handleSetAccordion, viewCount],
	);

	useEffect(() => {
		if (!accordionArray.length) {
			return;
		}

		if (useFirstOpen) {
			handleAccordion(0);
		}
	}, [accordionArray.length, handleAccordion, useFirstOpen]);

	useEffect(() => {
		if (showAll) {
			setViewCount(defaultViewCount);

			return;
		}

		setViewCount(cloneChildren.length);
	}, [cloneChildren.length, defaultViewCount, handleAccordion, showAll, useFirstOpen]);

	useEffect(() => {
		setAccordionLength(cloneChildren.length);
	}, [cloneChildren.length, setAccordionLength]);

	return (
		<ul
			ref={rootRef}
			style={style}
			className={cx('uiaccordions', theme, className)}
		>
			{renderItems}
			{showAll && !showAllHideFlag && (
				<div
					className={cx('more')}
					style={moreStyle}
				>
					<button
						type="button"
						className={cx('btn_all')}
						onClick={handleShowAll}
					>
						{showAllText}
					</button>
				</div>
			)}
		</ul>
	);
};

const MemoAccordion = React.memo(Accordion, isEqual);

MemoAccordion.Item = React.memo(AccordionItem, isEqual);

export default MemoAccordion;

export const AccordionItemForStoryBook = AccordionItem;
